import React, { useState, useEffect } from 'react';
import { eq, map, camelCase, startCase, toUpper, snakeCase, debounce, includes } from 'lodash';
import { useQuery, useMutation } from '@apollo/client';
import { Section } from '.';
import { PAGE_LIST, GENERAL_SETTINGS, UPDATE_GENERAL_SETTINGS, SET_CLIENT_STATE } from '../../../graphqls';

export default function SiteSettings() {
  const [horizontalPadding, setHorizontalPadding] = useState('');
  const [bannerHeight, setBannerHeight] = useState(0);
  const [pageTitle, setPageTitle] = useState('');
  const {
    loading: loadingGeneralSettings,
    data: {
      generalSettings,
      generalSettings: {
        navPosition,
        fontFamily,
        fontSize,
        navTextColor,
        headerBackgroundColor,
        footerBackgroundColor,
        measureColumns,
      } = {},
    } = {},
  } = useQuery(GENERAL_SETTINGS, {
    onCompleted({ generalSettings: { bannerHeight: originBannerHeight, pageTitle: originPageTitle } }) {
      setBannerHeight(originBannerHeight);
      setPageTitle(originPageTitle || '');
    },
  });
  const { loading: loadingPageList, data: { pageList = [] } = {} } = useQuery(PAGE_LIST);
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS, { refetchQueries: [{ query: GENERAL_SETTINGS }] });
  const [setState] = useMutation(SET_CLIENT_STATE);

  useEffect(() => {
    if (generalSettings.horizontalPadding) {
      setHorizontalPadding(generalSettings.horizontalPadding);
    }

    if (generalSettings.bannerHeight) {
      setBannerHeight(generalSettings.bannerHeight);
    }

    if (generalSettings.pageTitle) {
      setPageTitle(generalSettings.pageTitle);
    }
  }, [generalSettings]);

  if (includes([loadingGeneralSettings, loadingPageList], true)) {
    return '';
  }

  const navigatable = pageList.length;
  const changeFontFamily = ({ value }) => updateGeneralSettings({ variables: { fontFamily: value } });
  const updateFontSize = ({ target: { value } }) => updateGeneralSettings({ variables: { fontSize: value } });
  const updateNavPosition = ({ target: { value } }) => updateGeneralSettings({ variables: { navPosition: value } });
  const updateNavTextColor = ({ rgb: { r, g, b, a } }) =>
    updateGeneralSettings({ variables: { navTextColor: `rgba(${r},${g},${b},${a})` } });
  const updateHeaderBackgroundColor = ({ rgb: { r, g, b, a } }) =>
    updateGeneralSettings({ variables: { headerBackgroundColor: `rgba(${r},${g},${b},${a})` } });
  const updateFooterBackgroundColor = ({ rgb: { r, g, b, a } }) =>
    updateGeneralSettings({ variables: { footerBackgroundColor: `rgba(${r},${g},${b},${a})` } });
  const updateMeasureColumns = ({ target: { value } }) => updateGeneralSettings({ variables: { measureColumns: value } });
  const fontFamilyConfig = {
    label: 'Default Font Family',
    dropdown: {
      isClearable: false,
      isSearchable: false,
      options: map(
        [
          'Arial',
          'Crimson Text',
          'Georgia',
          'Lato',
          'Montserrat',
          'Nunito Sans',
          'Open Sans',
          'Oswald',
          'Poppins',
          'PT Serif',
          'Raleway',
          'Roboto',
          'Source Sans Pro',
          'Tahoma',
          'Times New Roman',
          'Verdana',
        ],
        (option) => ({
          label: option,
          value: toUpper(snakeCase(option)),
        }),
      ),
      styles: {
        option: (styles, { data: { value } }) => ({
          ...styles,
          fontFamily: startCase(camelCase(value)),
          color: '#404040',
        }),
        singleValue: (styles) => ({
          ...styles,
          fontFamily: startCase(camelCase(fontFamily)),
        }),
      },
      onChange: changeFontFamily,
      value: {
        label: startCase(camelCase(fontFamily)),
        value: fontFamily,
      },
    },
  };
  const fontSizeConfig = {
    label: 'Default Font Size',
    select: {
      value: fontSize || '14',
      onChange: updateFontSize,
      style: { width: '100px' },
      options: Array(7)
        .fill('Barry')
        .map((nullItem, nullIndex) => ({
          value: nullIndex + 12,
          label: nullIndex + 12,
        })),
    },
  };
  const navPositionConfig = {
    label: 'Navigation Position',
    select: {
      style: { width: '100px' },
      value: navPosition,
      onChange: updateNavPosition,
      options: [
        {
          value: 'NONE',
          label: 'None',
        },
        {
          value: 'TOP',
          label: 'Top',
        },
        {
          value: 'LEFT',
          label: 'Right',
        },
      ],
    },
  };
  const logoConfig = {
    label: 'Logo',
    image: {
      dataTip: 'Upload',
      onClick: () => setState({ variables: { modalTypeShown: 'logo' } }),
      className: 'icon-btn',
    },
  };
  const navTextColorConfig = {
    label: 'Navigation Text Color',
    color: {
      onChange: updateNavTextColor,
      color: navTextColor || 'rgba(255,255,255,0)',
      tooltip: 'Set Navigation Color',
      tooltipPosition: 'left',
    },
  };
  const headerBackgroundColorConfig = {
    label: 'Header Background Color',
    color: {
      onChange: updateHeaderBackgroundColor,
      color: headerBackgroundColor || 'rgba(255,255,255,0)',
      tooltipPosition: 'left',
    },
  };
  const commonBannerConfig = {
    label: 'Child Page Header background',
    image: {
      dataTip: 'Upload',
      onClick: () => setState({ variables: { modalTypeShown: 'commonBanner' } }),
      className: 'icon-btn',
    },
  };
  const footerTextConfig = {
    label: 'Footer Text',
    text: {
      dataTip: 'Edit',
      onClick: () => setState({ variables: { modalTypeShown: 'footerText' } }),
      className: 'icon-btn',
    },
  };
  const footerBackgroundColorConfig = {
    label: 'Footer Background Color',
    color: {
      onChange: updateFooterBackgroundColor,
      color: footerBackgroundColor || 'rgba(255,255,255,0)',
      position: 'left-center',
      tooltipPosition: 'left',
    },
  };
  const measureColumnsConfig = {
    label: 'Measure Columns',
    select: {
      value: measureColumns,
      onChange: updateMeasureColumns,
      style: { width: '100px' },
      options: [
        {
          value: 'ONE',
          label: '1',
        },
        {
          value: 'TWO',
          label: '2',
        },
        {
          value: 'THREE',
          label: '3',
        },
      ],
    },
  };
  const debounceHorizontalPadding = debounce((value) => updateGeneralSettings({ variables: { horizontalPadding: `${value}%` } }), 500);
  const updateHorizontalPadding = ({ target: { value = 0 } }) => {
    debounceHorizontalPadding(value);
    setHorizontalPadding(value);
  };
  const debounceBannerHeight = debounce((value) => updateGeneralSettings({ variables: { bannerHeight: Number(value) || 0 } }), 500);
  const updateBannerHeight = ({ target: { value = 0 } }) => {
    debounceBannerHeight(value);
    setBannerHeight(Number(value) || 0);
  };
  const debouncePageTitle = debounce((value) => updateGeneralSettings({ variables: { pageTitle: value || '' } }), 500);
  const updatePageTitle = ({ currentTarget: { value = '' } }) => {
    debouncePageTitle(value);
    setPageTitle(value || '');
  };
  const pageTitleConfig = {
    label: 'Set Browser Tab Title',
    input: {
      value: pageTitle === 'false' ? '' : pageTitle || '',
      onChange: updatePageTitle,
      style: { width: '100px' },
      className: 'form-control',
      placeholder: 'Public Dashboard',
    },
  };
  const horizontalPaddingConfig = {
    label: 'Page Body Width (%)',
    number: {
      value: Number(horizontalPadding.replace('%', '')),
      onChange: updateHorizontalPadding,
      style: { width: '100px' },
      className: 'page-padding-input',
    },
  };
  const bannerHeightConfig = {
    description: (
      <>
        Enter a number between 0px and 1000px.
        <br />
        Use 0 to return to the default value.
      </>
    ),
    label: 'Set Mobile Banner Height',
    number: {
      value: Number(bannerHeight) || 0,
      onChange: updateBannerHeight,
      style: { width: '100px' },
      className: 'page-padding-input',
    },
  };
  const cssOverwriteConfig = {
    label: 'CSS Overwrite',
    code: {
      onClick: () => setState({ variables: { modalTypeShown: 'cssEditor' } }),
      dataTip: 'Edit',
      className: 'color-picker-btn',
    },
  };

  return (
    <div className="general-settings site-setting">
      <strong className="general-setting-label">Site Settings</strong>
      <Section config={fontFamilyConfig} customClass="font-section" />
      <Section config={fontSizeConfig} />
      <Section config={logoConfig} customClass="logo-section" />
      {navigatable ? (
        <>
          <Section config={navPositionConfig} />
          {eq(navPosition, 'NONE') ? '' : <Section config={navTextColorConfig} />}
        </>
      ) : (
        ''
      )}
      <Section config={headerBackgroundColorConfig} />
      <Section config={commonBannerConfig} customClass="childpage-header-section" />
      <Section config={footerTextConfig} />
      <Section config={footerBackgroundColorConfig} />
      <Section config={measureColumnsConfig} />
      <Section config={horizontalPaddingConfig} customClass="page-padding-section" />
      <Section config={bannerHeightConfig} customClass="page-padding-section" />
      <Section config={pageTitleConfig} customClass="font-section" />
      <Section config={cssOverwriteConfig} />
    </div>
  );
}
